import { useEffect, useRef, useCallback, useMemo } from 'react';

type UseResizeObserverCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => void;

type UseResizeObserverOptions = ResizeObserverOptions & {
  /**
   * Si es true, usa requestAnimationFrame para agrupar múltiples cambios de tamaño
   * en un solo frame. Útil para evitar layout thrashing cuando hay múltiples
   * ResizeObservers activos o cuando el elemento cambia de tamaño rápidamente.
   * Por defecto es false para mantener la máxima responsividad.
   */
  useRequestAnimationFrame?: boolean;
};

/**
 * Hook personalizado que usa ResizeObserver API para observar cambios en el tamaño de un elemento.
 * Es más eficiente que escuchar eventos de resize de la ventana porque solo se dispara cuando
 * el elemento específico cambia de tamaño.
 *
 * @param callback - Función que se ejecuta cuando el elemento cambia de tamaño
 * @param options - Opciones del ResizeObserver y configuración adicional
 * @returns Función ref callback para asignar al elemento a observar
 */
export function useResizeObserver(
  callback: UseResizeObserverCallback,
  options?: UseResizeObserverOptions,
): (node: Element | null) => void {
  const callbackRef = useRef(callback);
  const observerRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingEntriesRef = useRef<ResizeObserverEntry[]>([]);
  const pendingObserverRef = useRef<ResizeObserver | null>(null);

  // Mantener el callback actualizado usando ref para evitar recrear el observer
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const useRAF = useMemo(()=> options?.useRequestAnimationFrame ?? false, [options?.useRequestAnimationFrame]);
  const resizeObserverOptions = useMemo(() => useRAF ? { box: options?.box } : options, [useRAF, options]);

  const ref = useCallback(
    (node: Element | null) => {
      // Limpiar el observer anterior si existe
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // Cancelar cualquier RAF pendiente
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      if (node) {
        observerRef.current = new ResizeObserver((entries, observer) => {
          if (useRAF) {
            // Guardar las entradas pendientes
            pendingEntriesRef.current = entries;
            pendingObserverRef.current = observer;

            // Cancelar el RAF anterior si existe
            if (rafRef.current !== null) {
              cancelAnimationFrame(rafRef.current);
            }

            // Programar la ejecución del callback en el siguiente frame
            rafRef.current = requestAnimationFrame(() => {
              callbackRef.current(pendingEntriesRef.current, pendingObserverRef.current!);
              rafRef.current = null;
              pendingEntriesRef.current = [];
            });
          } else {
            // Ejecutar inmediatamente sin RAF
            callbackRef.current(entries, observer);
          }
        });
        observerRef.current.observe(node, resizeObserverOptions);
      }
    },
    [useRAF, resizeObserverOptions],
  );

  // Limpiar el observer y cancelar RAF al desmontar
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return ref;
}

